<template>
  <div class="row transaction">
    <div
      v-if="loading"
      class="spinner-grow"
      style="background-color: #65b3c7; margin: -30px 0 0"
    ></div>

    <div v-if="error" class="alert alert-danger" role="alert">
      {{ errorMsg }}
    </div>

    <div class="filters mb-3 d-flex" style="gap: 10px">
      <button
        v-for="status in kycStatuses"
        :key="status.value"
        :class="`tab-btn ${status.class}`"
        @click="
          kycStatus = status.value;
          refreshData('filter');
        "
      >
        {{ status.label }}
      </button>
    </div>

    <section class="account-section">
      <h1 class="header-text">{{ headerText.toLocaleUpperCase() }}</h1>

      <div class="d-flex" style="gap: 20px">
        <div>
          <select
            class="form-select form-control business-select"
            placeholder="Select a search parameter"
            v-model="selectedValue"
          >
            <option value="">Select a search parameter</option>
            <option value="firstName">First Name</option>
            <option value="walletId">Wallet ID</option>
            <option value="phoneNumber">Phone Number</option>
          </select>
        </div>

        <div v-if="selectedValue">
          <input
            type="text"
            id="inlineFormInputGroup"
            class="form-control"
            v-model="searchValues[selectedValue]"
            :placeholder="formatPlaceholder(selectedValue)"
          />
        </div>

        <div class="d-flex" style="gap: 10px">
          <button type="submit" class="btns" @click="refreshData('filter')">
            Search
          </button>

          <button type="submit" class="btns" @click="downloadList">
            Download List
          </button>

          <button type="submit" class="btns" @click="refreshData('refresh')">
            Refresh
          </button>
        </div>
      </div>

      <div class="table-responsive">
        <table class="table">
          <thead class="thead-dark">
            <tr>
              <th scope="col">S/N</th>
              <th scope="col">First Name</th>
              <th scope="col">Middle Name</th>
              <th scope="col">Last Name</th>
              <th scope="col">Phone Number</th>
              <th scope="col">Wallet ID</th>
              <th scope="col">Account Type</th>
              <th scope="col">Tier</th>
              <th scope="col">KYC Status</th>
              <th scope="col">Date Created</th>
              <th scope="col">Last Updated</th>
              <th scope="col">Action</th>
            </tr>
          </thead>

          <tbody>
            <tr v-if="loading">
              <td colspan="100%">
                <div
                  class="d-flex justify-content-center align-items-center"
                  style="height: 200px"
                >
                  <span class="spin"></span>
                </div>
              </td>
            </tr>

            <tr v-else-if="agentList.length === 0 && !loading">
              <td colspan="100%" class="text-center">
                <div
                  class="d-flex justify-content-center align-items-center"
                  style="height: 200px"
                >
                  <span class="no-agent">No Agent Found</span>
                </div>
              </td>
            </tr>

            <tr
              v-else
              v-for="(agent, index) in filteredAgentList"
              :key="agent._id"
            >
              <td>{{ index + 1 + (currentPage - 1) * limit }}</td>
              <td>{{ agent.firstName }}</td>
              <td>{{ agent.middleName }}</td>
              <td>{{ agent.lastName }}</td>
              <td>{{ agent.phoneNumber }}</td>
              <td>{{ agent.walletId }}</td>
              <td>{{ transformAccountType(agent.accountType) }}</td>
              <td>{{ formatTier(agent.accountType, agent.tier) }}</td>
              <td>
                <span :class="getKycStatusClass(agent.verifiedStatus)">{{
                  agent.verifiedStatus
                }}</span>
              </td>
              <td>{{ formatDate(agent.createdAt) }}</td>
              <td>{{ formatDate(agent.updatedAt) }}</td>
              <td class="details">
                <router-link :to="`/dashboard/business-agent-kyc/${agent._id}`"
                  >View Details</router-link
                >
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="pagination">
        <div class="to-flex">
          <button
            class="btn pagination-btn"
            :disabled="!hasPrevPage"
            @click="refreshData('prev')"
          >
            Previous
          </button>

          <span>Page {{ currentPage }} of {{ totalPages }}</span>

          <button
            class="btn pagination-btn"
            :disabled="!hasNextPage"
            @click="refreshData('next')"
          >
            Next
          </button>
        </div>

        <div class="p-2">
          <div class="btn pagination-btn">Total Records: {{ totalDocs }}</div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import axios from "../store/axios";
import moment from "moment";
import fileDownload from "js-file-download";
import Swal from "sweetalert2";
import { convertArrayToCSV } from "convert-array-to-csv";
import { formatTier } from "../utils/utils";

export default {
  components: {},
  data() {
    return {
      loading: false,
      error: false,
      download: false,
      startDate: moment().format("YYYY-MM-DD"),
      endDate: moment().format("YYYY-MM-DD"),
      errorMsg: "",
      selectedValue: "",
      headerText: "business account kyc",
      moment,
      currentPage: 1,
      limit: 50,
      totalPages: 0,
      hasNextPage: false,
      hasPrevPage: false,
      totalDocs: 0,
      dateRange: [],
      agentList: [],
      filteredAgentList: [],
      kycStatus: "",
      searchValues: {
        walletId: "",
        phoneNumber: "",
        firstName: "",
      },
      kycStatuses: [
        { value: "verified", label: "Verified KYC", class: "btn-verified" },
        { value: "pending", label: "Pending KYC", class: "btn-pending" },
        { value: "rejected", label: "Rejected KYC", class: "btn-rejected" },
        { value: "unverified", label: "Unverified", class: "btn-unverified" },
      ],
    };
  },
  computed: {},
  methods: {
    formatTier,
    formatDate(date) {
      return moment(date).format("DD-MM-YYYY");
    },
    resetFiltersAndPagination() {
      this.currentPage = 1;
      this.selectedValue = "";
      this.status = "";
      this.clearSearchFields();
    },
    buildPayload(mode) {
      const basePayload = {
        accountType: "business-account",
        page: this.currentPage,
        limit: this.limit,
        verifiedStatus: this.kycStatus,
        ...this.searchValues,
      };

      if (mode !== "refresh") {
        return {
          status: this.status,
          ...basePayload,
          ...this.searchValues,
          ...(mode === "download" && { download: true }),
          ...(mode === "next" && { page: ++this.currentPage }),
          ...(mode === "prev" && { page: --this.currentPage }),
        };
      }

      return basePayload;
    },
    handleApiResponse(response, mode) {
      if (mode === "download") {
        fileDownload(
          convertArrayToCSV(this.filteredAgentList),
          "agent_list.csv",
        );
      } else {
        const {
          docs,
          hasNextPage,
          hasPrevPage,
          totalPages,
          prevPage,
          nextPage,
          totalDocs,
          page,
        } = response.data.agentList;

        Object.assign(this, {
          agentList: docs,
          filteredAgentList: docs,
          hasNextPage,
          hasPrevPage,
          totalPages,
          prevPage,
          nextPage,
          totalDocs,
          currentPage: page,
        });
      }
    },
    async refreshData(mode) {
      try {
        this.loading = true;

        if (mode === "refresh") this.resetFiltersAndPagination();
        if (mode === "filter") this.currentPage = 1;

        const selectedStatus = this.kycStatuses.find(
          (status) => status.value === this.kycStatus,
        );

        this.headerText = selectedStatus
          ? selectedStatus.label
          : "Business Account KYC";

        const response = await axios.post(
          `${process.env.VUE_APP_API_URL}/agent-list`,
          this.buildPayload(mode),
        );

        this.handleApiResponse(response, mode);

        if (mode === "filter" && this.selectedValue) {
          this.applySearch();
        }
      } catch (err) {
        this.error = true;
        this.errorMsg = "Error fetching data.";
      } finally {
        this.loading = false;
      }
    },
    getKycStatusClass(status) {
      return `${status.toLowerCase()} status-btn`;
    },
    transformAccountType(accountType) {
      return accountType === "business-account" ? "Business" : "N/A";
    },
    applySearch() {
      const searchField = this.selectedValue;

      if (!searchField) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Please select a search parameter before searching.",
        });
        return;
      }

      if (!this.searchValues[searchField]) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: `Please enter a value for ${searchField.replace(
            /Id/g,
            " ID",
          )}.`,
        });
        return;
      }

      this.currentPage = 1;

      const searchValue = this.searchValues[searchField]
        .toString()
        .toLowerCase();

      this.filteredAgentList = this.agentList.filter((agent) => {
        const agentField = agent[searchField].toString().toLowerCase() || "";

        return agentField.includes(searchValue);
      });

      if (this.filteredAgentList.length === 0) {
        Swal.fire({
          icon: "error",
          title: "Error",
          html: `<strong>No records found for your search criteria.</strong>`,
        });
      }
    },
    downloadList() {
      fileDownload(convertArrayToCSV(this.filteredAgentList), "agent_list.csv");
    },
    formatDate(date) {
      return moment(date).format("DD-MM-YYYY");
    },
    formatPlaceholder(value) {
      if (value === "walletId") return "Enter Wallet ID";
      if (value === "phoneNumber") return "Enter Phone Number";
      if (value === "firstName") return "Enter First Name";
      return "Enter Value";
    },
    clearSearchFields() {
      Object.keys(this.searchValues).forEach((key) => {
        this.searchValues[key] = "";
      });
    },
  },
  watch: {
    selectedValue() {
      this.clearSearchFields();
    },
  },
  async mounted() {
    this.refreshData();
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Arial&display=swap");
@import url("https://cdnjs.cloudflare.com/ajax/libs/font-awesome/5.15.4/css/all.min.css");

h1 {
  margin: 0;
}

input {
  background-color: #fafafa;
  font-size: 10px;
}

.table-responsive {
  overflow: scroll;
}

tr > td {
  border-top: 0.5px solid #e8e9eb !important;
}

th,
td {
  letter-spacing: 0px;
  font-size: 12px !important;
  font-weight: 400;
  font-family: Arial, sans-serif;
  text-align: center;
  white-space: nowrap;
}

td {
  padding: 20px !important;
}

.no-agent {
  font-weight: 600;
  text-transform: uppercase;
}

.spinner-grow {
  position: fixed;
}

#list {
  list-style-type: none;
}

.account-section {
  background-color: #fff;
  width: 90vw;
  min-height: 100vh;
  border-radius: 8px;
  padding: 30px;
  display: flex;
  flex-direction: column;
  gap: 20px 0px;
}

.header-text {
  font-size: 16px !important;
  font-weight: 700 !important;
  color: #000;
}

.status-btn {
  display: inline;
  padding: 6px 16px !important;
  border: none;
  border-radius: 4px;
  font-weight: 500 !important;
  text-transform: capitalize;
  font-size: 12px;
}

.btns {
  background-color: #65b3c7;
  border: #65b3c7;
  text-transform: uppercase !important;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700 !important;
  color: #fff;
  padding: 0 36px;
  border-radius: 4px;
  font-size: 10px;
  word-spacing: 0.9px;
}

.btn-verified {
  background-color: #4cbb17;
}

.btn-pending {
  background-color: #ffd700;
}

.btn-rejected {
  background-color: #ff0800;
}

.btn-unverified {
  background-color: #4b61d1;
}

.business-select {
  background-color: #f5f5f5 !important;
  color: #9a9a9a !important;
  font-size: 12px !important;
}

.verified {
  color: #4cbb17;
  background-color: #1eb75b1a;
}

.rejected {
  color: #ff0800;
  background-color: #e02b001a;
}

.pending {
  color: #ffd700;
  background-color: #f2b6301a;
}

.unverified {
  color: #4b61d1;
  background-color: #eef2ff;
}

.details {
  letter-spacing: 0.1px;
  font-size: 10px !important;
  word-spacing: 0.1px;
  font-weight: 600;
  background-color: transparent;
  cursor: pointer;
  color: #0047ab !important;
}

.to-flex {
  display: flex;
  gap: 20px;
  align-items: center;
  justify-content: space-between;
}

.pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.pagination-btn {
  font-size: 10px !important;
  letter-spacing: 0.8px;
}

.spin {
  border: 2px solid rgba(101, 179, 199, 0.2);
  border-left-color: #65b3c7;
  width: 25px;
  height: 25px;
}
</style>
